/**
 * Saying WHERE a raw-JSON editor's text is broken, and reformatting it.
 *
 * <h3>Why "Not valid JSON" was not enough</h3>
 * The runtime document editor holds ~70 kB — around two and a half thousand
 * lines — of an agent's live configuration. Told only that the document is
 * invalid, the one thing you can do is undo until the message goes away, which
 * on a paste of someone else's document means throwing the paste away.
 *
 * <h3>Why the engine's message is not enough either</h3>
 * `JSON.parse` throws a SyntaxError whose wording is not standardised and has
 * changed under us. On the V8 this app runs against today:
 *
 *   trailing comma  → "Expected double-quoted property name in JSON at
 *                      position 226 (line 4 column 1)"
 *   NaN             → "Unexpected token 'N', ..."\"a\": NaN\n}" is not valid
 *                      JSON"                                  ← NO position
 *   Safari          → "JSON Parse error: Expected '}'"        ← NO position
 *
 * So a reader that only greps the message is right on some faults, silent on
 * others, and at the mercy of the next V8. A location that is confidently
 * wrong is worse than none at all — it sends you to the wrong line of a 2500
 * line document and you edit something that was fine.
 *
 * <h3>What this does instead</h3>
 * `JSON.parse` stays the sole authority on whether the text is VALID — nothing
 * here ever declares a document good. Only once the engine has rejected it do
 * we run our own scanner over the text to find WHERE, which is engine
 * independent, points at the comma rather than at the brace after it, and
 * survives the next message rewording. The engine's own words are kept
 * verbatim alongside, and if the scanner finds nothing to blame (it disagrees
 * with the engine; the input blew the stack) we fall back to whatever position
 * the message carries, and then to no location at all.
 */

/** A place in the text, all 1-based except `position`. */
export interface JsonPoint {
  /** 0-based character index, as `JSON.parse` counts. */
  position: number;
  line: number;
  column: number;
  /** The whole line, so the editor can show what it is complaining about. */
  lineText: string;
}

export interface JsonError {
  /** What is wrong, in words meant for the person editing. */
  message: string;
  /** The engine's own message, untouched — the fallback when we explain badly. */
  raw: string;
  /** Null when we could not honestly work out where. */
  at: JsonPoint | null;
}

/** Where a character index falls, and the line it falls on. */
export function pointAt(text: string, position: number): JsonPoint {
  const pos = Math.max(0, Math.min(Math.floor(position), text.length));
  let line = 1;
  let lineStart = 0;
  for (let i = 0; i < pos; i++) {
    if (text.charCodeAt(i) === 10) { line++; lineStart = i + 1; }
  }
  let end = text.indexOf('\n', lineStart);
  if (end < 0) end = text.length;
  return {
    position: pos,
    line,
    column: pos - lineStart + 1,
    lineText: text.slice(lineStart, end).replace(/\r$/, ''),
  };
}

/** The character index a 1-based line and column refer to. */
export function positionOf(text: string, line: number, column: number): number | null {
  if (!Number.isFinite(line) || line < 1) return null;
  let at = 0;
  for (let l = 1; l < line; l++) {
    const nl = text.indexOf('\n', at);
    if (nl < 0) return null; // the message named a line this text does not have
    at = nl + 1;
  }
  return Math.min(at + Math.max(0, column - 1), text.length);
}

// ── reading the engine's message, defensively ───────────────────────────────

// V8:            "… in JSON at position 226 (line 4 column 1)"
// V8 (older):    "Unexpected token } in JSON at position 226"
// SpiderMonkey:  "JSON.parse: … at line 4 column 1 of the JSON data"
// JavaScriptCore: no location at all.
const POSITION_IN_MESSAGE = /position (\d+)/i;
const LINE_COLUMN_IN_MESSAGE = /line (\d+) column (\d+)/i;

/**
 * A location taken from the engine's own wording, or null.
 *
 * Anything it produces is checked against the text before being believed: a
 * position past the end, or a line the document does not have, means we
 * mis-read the message and must say nothing rather than guess.
 */
export function locateFromMessage(message: string, text: string): JsonPoint | null {
  const byPosition = POSITION_IN_MESSAGE.exec(message);
  if (byPosition) {
    const pos = Number(byPosition[1]);
    if (Number.isFinite(pos) && pos >= 0 && pos <= text.length) return pointAt(text, pos);
  }
  const byLine = LINE_COLUMN_IN_MESSAGE.exec(message);
  if (byLine) {
    const pos = positionOf(text, Number(byLine[1]), Number(byLine[2]));
    if (pos !== null) return pointAt(text, pos);
  }
  return null;
}

/** The engine's message with the machinery we display separately taken off. */
export function humanise(raw: string): string {
  return raw
    // "… in JSON at position 226 (line 4 column 1)" — shown as line/column.
    .replace(/\s*in JSON at position \d+(\s*\(line \d+ column \d+\))?/i, '')
    // "Unexpected token 'N', ..."…" is not valid JSON" — the echoed source.
    .replace(/,\s*(\.\.\.)?"[\s\S]*"\s*(\.\.\.)?\s*is not valid JSON\.?$/i, '')
    .replace(/^JSON\.parse:\s*/i, '')
    .replace(/\s*of the JSON data\.?$/i, '')
    .replace(/\s*at line \d+ column \d+/i, '')
    .trim() || raw;
}

// ── finding the fault ourselves ─────────────────────────────────────────────

export interface JsonFault {
  position: number;
  message: string;
}

class ScanFault {
  constructor(readonly position: number, readonly message: string) {}
}

const isDigit = (c: string | undefined): boolean => !!c && c >= '0' && c <= '9';

/** What to say about a character that cannot begin a value. */
function unexpectedValue(text: string, i: number): string {
  const c = text[i];
  if (c === "'") return 'JSON strings must be in double quotes, not single quotes.';
  if (c === ',') return 'There is a comma here with no value before it.';
  if (c === '}' || c === ']') return `Nothing was written before this ${c}.`;
  for (const word of ['NaN', 'Infinity', '-Infinity', 'undefined', 'None', 'True', 'False', 'null_']) {
    if (text.startsWith(word, i)) return `${word} is not valid JSON — use a number, a quoted string, true, false or null.`;
  }
  if (/[A-Za-z_$]/.test(c)) return 'Unquoted text is not a JSON value — put it in double quotes, or use true, false or null.';
  return `${JSON.stringify(c)} cannot start a value.`;
}

/**
 * The first thing wrong with `text`, and where — or null if we find nothing.
 *
 * This never decides that a document is VALID; `checkJson` has already had
 * `JSON.parse` reject it before this runs. Returning null here only means "we
 * cannot say where", which sends the caller to the engine's message.
 */
export function scanJson(text: string): JsonFault | null {
  let i = 0;
  const n = text.length;

  const fail = (position: number, message: string): never => {
    throw new ScanFault(Math.max(0, Math.min(position, n)), message);
  };
  const skipSpace = () => {
    while (i < n) {
      const c = text.charCodeAt(i);
      if (c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d) i++;
      else break;
    }
  };

  const scanString = () => {
    const opened = i;
    i++; // the opening quote
    for (;;) {
      if (i >= n) fail(opened, 'This string is never closed — it needs a matching double quote.');
      const c = text.charCodeAt(i);
      if (c === 0x22) { i++; return; }
      if (c === 0x5c) {
        const esc = text[i + 1];
        if (esc === undefined) fail(opened, 'This string is never closed — it needs a matching double quote.');
        if (esc === 'u') {
          if (!/^[0-9a-fA-F]{4}$/.test(text.slice(i + 2, i + 6))) fail(i, '\\u must be followed by four hex digits.');
          i += 6;
          continue;
        }
        if (!'"\\/bfnrt'.includes(esc)) {
          fail(i, `\\${esc} is not an escape JSON knows — it allows \\" \\\\ \\/ \\b \\f \\n \\r \\t and \\uXXXX.`);
        }
        i += 2;
        continue;
      }
      // A raw newline inside a string is what an unterminated string looks
      // like in a pretty-printed document, so blame the line it opened on.
      if (c === 0x0a || c === 0x0d) fail(i, 'This string is never closed — a JSON string cannot run past the end of its line.');
      if (c < 0x20) fail(i, 'A control character cannot sit inside a string unescaped.');
      i++;
    }
  };

  const scanNumber = () => {
    const start = i;
    if (text[i] === '-') i++;
    if (text[i] === '0') i++;
    else if (isDigit(text[i])) { while (isDigit(text[i])) i++; }
    else fail(start, 'A number needs at least one digit.');
    if (text[i] === '.') {
      i++;
      if (!isDigit(text[i])) fail(i, 'A decimal point must be followed by a digit.');
      while (isDigit(text[i])) i++;
    }
    if (text[i] === 'e' || text[i] === 'E') {
      i++;
      if (text[i] === '+' || text[i] === '-') i++;
      if (!isDigit(text[i])) fail(i, 'An exponent must be followed by a digit.');
      while (isDigit(text[i])) i++;
    }
  };

  const scanValue = () => {
    skipSpace();
    if (i >= n) fail(n, 'The document ends where a value was expected.');
    const c = text[i];
    if (c === '{') return scanObject();
    if (c === '[') return scanArray();
    if (c === '"') return scanString();
    if (c === '-' || isDigit(c)) return scanNumber();
    for (const lit of ['true', 'false', 'null']) {
      if (text.startsWith(lit, i)) { i += lit.length; return; }
    }
    return fail(i, unexpectedValue(text, i));
  };

  function scanObject() {
    const opened = i;
    i++; // {
    skipSpace();
    if (text[i] === '}') { i++; return; }
    let lastComma = -1;
    for (;;) {
      skipSpace();
      if (i >= n) fail(opened, 'This { is never closed — the document ends first.');
      if (text[i] !== '"') {
        if (text[i] === '}' && lastComma >= 0) fail(lastComma, 'Remove this comma — JSON does not allow one before the closing }.');
        if (text[i] === "'") fail(i, 'A property name must be in double quotes, not single quotes.');
        fail(i, 'A property name must be a double-quoted string.');
      }
      scanString();
      skipSpace();
      if (text[i] !== ':') fail(i, 'Expected a : between the property name and its value.');
      i++;
      scanValue();
      skipSpace();
      if (i >= n) fail(opened, 'This { is never closed — the document ends first.');
      if (text[i] === ',') { lastComma = i; i++; continue; }
      if (text[i] === '}') { i++; return; }
      fail(i, 'Expected a , before the next property, or a } to close this object.');
    }
  }

  function scanArray() {
    const opened = i;
    i++; // [
    skipSpace();
    if (text[i] === ']') { i++; return; }
    let lastComma = -1;
    for (;;) {
      skipSpace();
      if (text[i] === ']' && lastComma >= 0) fail(lastComma, 'Remove this comma — JSON does not allow one before the closing ].');
      scanValue();
      skipSpace();
      if (i >= n) fail(opened, 'This [ is never closed — the document ends first.');
      if (text[i] === ',') { lastComma = i; i++; continue; }
      if (text[i] === ']') { i++; return; }
      fail(i, 'Expected a , before the next item, or a ] to close this array.');
    }
  }

  try {
    skipSpace();
    if (i >= n) return { position: 0, message: 'There is nothing here to read.' };
    scanValue();
    skipSpace();
    if (i < n) return { position: i, message: 'There is more text after the end of the JSON value.' };
    return null;
  } catch (e) {
    if (e instanceof ScanFault) return { position: e.position, message: e.message };
    // Adversarial nesting can exhaust the stack. Say nothing rather than lie.
    return null;
  }
}

// ── what the editors call ───────────────────────────────────────────────────

/**
 * Null when the text parses; otherwise what is wrong and, when we can say it
 * honestly, where.
 *
 * Blank text is reported as an error here. Whether blank is ACCEPTABLE is the
 * caller's policy — both editors treat an empty box as "nothing typed yet"
 * rather than as a mistake — so they check for blankness themselves.
 */
export function checkJson(text: string): JsonError | null {
  try {
    JSON.parse(text);
    return null;
  } catch (e) {
    const raw = e instanceof Error ? e.message : String(e);
    const found = scanJson(text);
    if (found) return { message: found.message, raw, at: pointAt(text, found.position) };
    return { message: humanise(raw), raw, at: locateFromMessage(raw, text) };
  }
}

export type JsonRewrite =
  | { ok: true; text: string }
  | { ok: false; error: JsonError };

/**
 * Re-indent the document.
 *
 * Refuses invalid input rather than quietly leaving the text alone: a button
 * that sometimes does nothing teaches you to stop trusting it. The result is
 * `JSON.parse` → `JSON.stringify`, so it can only ever change whitespace and
 * key order is preserved — nothing either editor SENDS is affected.
 */
export function formatJson(text: string, indent = 2): JsonRewrite {
  const error = checkJson(text);
  if (error) return { ok: false, error };
  return { ok: true, text: JSON.stringify(JSON.parse(text), null, indent) };
}

/** The same document on one line, for pasting somewhere that wants it small. */
export function minifyJson(text: string): JsonRewrite {
  const error = checkJson(text);
  if (error) return { ok: false, error };
  return { ok: true, text: JSON.stringify(JSON.parse(text)) };
}

/** How many lines the gutter has to number. */
export function countLines(text: string): number {
  let lines = 1;
  for (let i = 0; i < text.length; i++) if (text.charCodeAt(i) === 10) lines++;
  return lines;
}

/** "1\n2\n3…" — one text node for the gutter instead of a node per line. */
export function gutterText(lines: number): string {
  const out: string[] = new Array(Math.max(1, lines));
  for (let i = 0; i < out.length; i++) out[i] = String(i + 1);
  return out.join('\n');
}

/** The character range of a 1-based line, for selecting it in a textarea. */
export function lineRange(text: string, line: number): { start: number; end: number } {
  const start = positionOf(text, line, 1) ?? 0;
  let end = text.indexOf('\n', start);
  if (end < 0) end = text.length;
  return { start, end };
}

/**
 * A readable window of a long line, centred on the fault.
 *
 * Runtime documents carry prompts thousands of characters long on one line.
 * Printing the whole of one under an error message pushes the panel — and
 * every column count in it — off the side of the screen.
 */
export interface LineExcerpt {
  text: string;
  /** 1-based column of the fault WITHIN `text`. */
  column: number;
  truncatedLeft: boolean;
  truncatedRight: boolean;
}

export function excerptLine(lineText: string, column: number, width = 96): LineExcerpt {
  if (lineText.length <= width) {
    return { text: lineText, column, truncatedLeft: false, truncatedRight: false };
  }
  const fault = Math.max(0, column - 1);
  let start = Math.max(0, fault - Math.floor(width / 2));
  const end = Math.min(lineText.length, start + width);
  start = Math.max(0, end - width);
  return {
    text: lineText.slice(start, end),
    column: fault - start + 1,
    truncatedLeft: start > 0,
    truncatedRight: end < lineText.length,
  };
}
